import {PageType} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import type {Cta, WorkspaceTransferUiConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {resolveTransferUiConfig} from "~/domainModels/transfer/defaultTransferConfig";
import {isTransferCapableManifest} from "~/domainModels/transfer/transferConfigRegistry";
import {findTransferFormPageId, findTransferPreviewPageId, findTransferRowCta} from "~/domainModels/transfer/transferConfigLookup";

type TransferManifestView = {
    type: string;
    moreCtaIds?: Array<Uuid>;
    cardLayout?: {
        moreCtaIds?: Array<Uuid>;
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

type TransferManifestPage = {
    id: Uuid;
    type: string;
    title?: string;
    parentPageId?: Uuid | null;
    viewEngine?: {
        inQueueAssignmentFilter?: {leadOwnerFieldId?: Uuid} | null;
        views: Array<TransferManifestView>;
    };
};

type TransferManifestSlice = {
    pages: Record<string, TransferManifestPage>;
    ctas: Record<string, Cta>;
    columnMapping: Record<string, unknown>;
    transfer?: WorkspaceTransferUiConfig;
};

const TRANSFER_MORE_MENU_VIEW_TYPES = new Set(["table", "list", "kanban"]);

/** Hydrate defaults when the workspace UI config omits Transfer pages / CTA. */
const DEFAULT_TRANSFER_FORM_PAGE_ID = PageType.TransferForm;
const DEFAULT_TRANSFER_PREVIEW_PAGE_ID = PageType.TransferPreview;
const DEFAULT_TRANSFER_ROW_CTA_ID = uniqueUuid`c4d5e6f7-0819-42a3-bcd4-e5f60718293a`;

function isInQueueListPage(page: TransferManifestPage): boolean {
    return page.viewEngine?.inQueueAssignmentFilter != null;
}

function findLifecycleOverviewPageIds(manifest: TransferManifestSlice): Array<Uuid> {
    const validPages = Object.values(manifest.pages).filter(
        (page) => page.type === PageType.EventContainerWithEvents && isInQueueListPage(page) !== true,
    );
    validPages.sort((a, b) => {
        const aIsArchive = (a.title ?? "").toLowerCase().includes("archive");
        const bIsArchive = (b.title ?? "").toLowerCase().includes("archive");
        if (aIsArchive && !bIsArchive) return 1;
        if (!aIsArchive && bIsArchive) return -1;
        return 0;
    });
    return validPages.map((page) => page.id as Uuid);
}

function appendTransferCtaId(moreCtaIds: Array<Uuid> | undefined, transferCtaId: Uuid): Array<Uuid> {
    const existing = moreCtaIds ?? [];
    if (existing.some((ctaId) => String(ctaId) === String(transferCtaId)) === true) {
        return existing;
    }
    return [...existing, transferCtaId];
}

/** Inject Transfer into table / list / kanban ⋯ menus (covers desktop table + mobile list + kanban). */
function patchViewsWithTransferMoreCta(
    views: Array<TransferManifestView>,
    transferCtaId: Uuid,
): Array<TransferManifestView> {
    return views.map((view) => {
        if (TRANSFER_MORE_MENU_VIEW_TYPES.has(view.type) !== true) {
            return view;
        }

        if (view.type === "table") {
            return {
                ...view,
                moreCtaIds: appendTransferCtaId(view.moreCtaIds, transferCtaId),
            };
        }

        if (view.cardLayout === undefined) {
            return view;
        }

        return {
            ...view,
            cardLayout: {
                ...view.cardLayout,
                moreCtaIds: appendTransferCtaId(view.cardLayout.moreCtaIds, transferCtaId),
            },
        };
    });
}

function buildTransferCta(ctaId: Uuid, transferFormPageId: Uuid): Cta {
    return {
        id: ctaId,
        label: "Transfer",
        description: null,
        appearance: null,
        iconColorToken: null,
        textColorToken: null,
        icon: "transfer",
        tableAction: null,
        placements: ["more"],
        inVisibility: null,
        disability: null,
        action: {
            type: "openPage",
            pageId: transferFormPageId,
            presentation: "page",
            preferInlineModal: false,
            queryParams: ["journeyId", "eventContainerId", "eventContainerTemplateId"],
        },
    };
}

/** Inject transfer hosted pages + row CTA into transfer-capable workspace manifests. */
export function mergeTransferModuleIntoManifest<T extends TransferManifestSlice>(manifest: T): T {
    if (isTransferCapableManifest(manifest) !== true) {
        return manifest;
    }

    const lifecyclePageIds = findLifecycleOverviewPageIds(manifest);
    const parentPageId = lifecyclePageIds[0] ?? null;
    const existingFormPageId = findTransferFormPageId(manifest);
    const existingPreviewPageId = findTransferPreviewPageId(manifest);
    const transferFormPageId = existingFormPageId ?? DEFAULT_TRANSFER_FORM_PAGE_ID;
    const transferPreviewPageId = existingPreviewPageId ?? DEFAULT_TRANSFER_PREVIEW_PAGE_ID;

    const pages: Record<string, TransferManifestPage> = {
        ...manifest.pages,
        ...(existingFormPageId !== undefined
            ? {}
            : {
                  [transferFormPageId]: {
                      id: transferFormPageId,
                      type: PageType.TransferForm,
                      title: "Transfer",
                      parentPageId,
                  },
              }),
        ...(existingPreviewPageId !== undefined
            ? {}
            : {
                  [transferPreviewPageId]: {
                      id: transferPreviewPageId,
                      type: PageType.TransferPreview,
                      title: "Preview",
                      parentPageId: transferFormPageId,
                  },
              }),
    };

    const existingTransferCta = findTransferRowCta({pages, ctas: manifest.ctas});
    const transferCtaId = existingTransferCta?.id ?? DEFAULT_TRANSFER_ROW_CTA_ID;
    const nextCtas = {...manifest.ctas};
    if (existingTransferCta !== undefined) {
        nextCtas[existingTransferCta.id] = {
            ...existingTransferCta,
            icon: existingTransferCta.icon ?? "transfer",
        };
    } else {
        nextCtas[transferCtaId] = buildTransferCta(transferCtaId, transferFormPageId);
    }

    for (const pageId of lifecyclePageIds) {
        const page = pages[pageId];
        if (page === undefined || page.type !== PageType.EventContainerWithEvents) {
            continue;
        }
        const listPage = page as {
            viewEngine: {views: Array<TransferManifestView>};
        };
        pages[pageId] = {
            ...page,
            viewEngine: {
                ...listPage.viewEngine,
                views: patchViewsWithTransferMoreCta(listPage.viewEngine.views, transferCtaId),
            },
        };
    }

    return {
        ...manifest,
        pages,
        ctas: nextCtas,
        transfer: resolveTransferUiConfig(manifest.transfer),
    };
}
